import { useState, useEffect, useRef } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Image as ImageIcon,
  Move,
  Settings,
  Clipboard,
  CheckCircle,
  UploadCloud
} from "react-feather";
import Alert from "../../../../components/Alert/Alert";
import useBackConfirm from "../../../../hooks/useBackConfirm";
import {
  INSTRUCTION_SNIPPETS,
  buildSmartInstructions
} from "./signInstructionBuilder";
import { HexColorPicker } from "react-colorful";
import "./SignType.css";


/* ===== SIGN CATEGORIES ===== */
const SIGN_CATEGORIES = [
  {
    id: "3d",
    label: "3D Signs | Letters",
    items: [
      { id: "channel", label: "Channel Letters" },
      { id: "neon", label: "Flex Neon Sign" },
      { id: "alumCut", label: "Aluminum Letters" },
      { id: "acrylic3D", label: "Acrylic Letters" },
      { id: "pvc", label: "PVC Letters" }
    ]
  },
  {
    id: "cabinet",
    label: "Cabinet Signs",
    items: [
      { id: "lightbox", label: "Light Box" },
      { id: "push", label: "Push-Through Sign" },
      { id: "blade", label: "Blade Sign" },
      { id: "pylon", label: "Pylon" }
    ]
  },
  {
    id: "printed",
    label: "Printed / Graphics",
    items: [
      { id: "film", label: "Print | Dicut Graphics" },
      { id: "banner", label: "Printed Banner" },
      { id: "printMat", label: "ACM | Aluminum + Graphics" },
      { id: "acrylicFace", label: "Acrylic + Graphics" }
    ]
  },

];


export default function SignType({ signs = [], onNext, onBack }) {
  const imageRefs = useRef({});
  const [, forceRender] = useState(0);
  const [modalMessage, setModalMessage] = useState("");
  const [data, setData] = useState(
    signs.map((s) => ({
      ...s,
      shapeType: s.shape?.type || "square",
      illuminated: s.illuminated ?? true,
      width: s.width || "",
      height: s.height || "",
      estimateWithAI: true,
      aiMode: false,
      openCategory: null,
      openDetails: false,
      addition: s.addition || null,
      additionColor: s.additionColor || "#000000",
      instructions: s.instructions || INSTRUCTION_SNIPPETS.illuminated
    }))
  );

  const [openInstructionsIndex, setOpenInstructionsIndex] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, []);

  useEffect(() => {
    const observers = [];

    Object.values(imageRefs.current).forEach((img) => {
      if (!img) return;

      const observer = new ResizeObserver(() => {
        forceRender(v => v + 1);
      });

      observer.observe(img);
      observers.push(observer);
    });

    return () => {
      observers.forEach(o => o.disconnect());
    };
  }, [data]);

  const backConfirm = useBackConfirm(onBack);

  function updateSign(index, changes) {
    setData((prev) =>
      prev.map((s, i) => (i === index ? { ...s, ...changes } : s))
    );
  }

  function setDimension(index, field, value) {
    const numeric = Number(value);
    const valid = numeric > 0;

    updateSign(index, {
      [field]: value,
      estimateWithAI: !(valid && Number(data[index].width) > 0 && Number(data[index].height) > 0)
    });
  }

  async function compressImage(file, maxWidth = 800, quality = 0.85) {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);

      img.onload = () => {
        const scale = Math.min(1, maxWidth / img.width);

        const canvas = document.createElement("canvas");
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(
          (blob) => {
            resolve(
              new File([blob], file.name, {
                type: "image/jpeg",
                lastModified: Date.now()
              })
            );
          },
          "image/jpeg",
          quality
        );
      };
    });
  }


  function fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  function isValidDimension(value) {
    return value !== "" && Number.isFinite(Number(value)) && Number(value) > 0;
  }

  function handleNext() {
    const missing = [];

    data.forEach((sign, index) => {
      const issues = [];

      if (!isValidDimension(sign.width) || !isValidDimension(sign.height)) {
        issues.push("Dimensions (Width and Height)");
      }

      if (!sign.signType) {
        issues.push("Type of Sign");
      }

      if (!sign.logo?.base64) {
        issues.push("Logo / Artwork");
      }

      if (issues.length) {
        missing.push(
          `Sign #${index + 1}\n${issues.map((issue, i) => `${i + 1} - ${issue}`).join("\n")}`
        );
      }
    });

    if (missing.length) {
      setModalMessage(
        `Please complete the following before continuing:\n\n${missing.join("\n\n")}`
      );
      return;
    }

    onNext(data);
  }



  return (
    <div className="sign-type">
      <div className="sign-flow-shell">
        <div className="steps-progress">
          <div className="step active">
            <div className="step-icon">
              <ImageIcon size={18}
                onClick={() => {
                  window.location.href = "/";
                }} />
            </div>
            <span>Upload Picture</span>
          </div>

          <div className="step-line" />

          <div className="step active">
            <div className="step-icon">
              <Move size={18} />
            </div>
            <span>Add Signs</span>
          </div>

          <div className="step-line" />

          <div className="step active">
            <div className="step-icon">
              <Settings size={18} />
            </div>
            <span>Project Setup</span>
          </div>

          <div className="step-line" />

          <div className="step">
            <div className="step-icon">
              <Clipboard size={18} />
            </div>
            <span>Project Review</span>
          </div>

          <div className="step-line" />

          <div className="step">
            <div className="step-icon">
              <CheckCircle size={18} />
            </div>
            <span>Results</span>
          </div>
        </div>

        <div className="upload-header-content">
          <div className="header">
            <h2>Select sign types & upload logos</h2>
            <p>Configure each sign individually.</p>
          </div>

          <div className="actions">
            <button className="secondary" onClick={backConfirm.askBack}>
              <ArrowLeft size={12} /> Back
            </button>

            <button className="primary" onClick={handleNext}>
              Next <ArrowRight size={12} />
            </button>
          </div>
        </div>
      </div>

      <div className="sign-cards">
        {data.map((sign, index) => {

          const is3DSign = [
            "channel",
            "neon",
            "alumCut",
            "acrylic3D",
            "pvc"
          ].includes(sign.signType);

          return (
            <div key={index} className={`sign-card ${sign.aiMode ? "ai-active" : ""}`}>

              {/* Preview */}
              <div className="sign-preview">
                <div className="image-wrapper">
                  <img
                    ref={(el) => (imageRefs.current[index] = el)}
                    src={sign.baseImage}
                    alt=""
                    onLoad={() => forceRender(v => v + 1)}
                  />

                  {imageRefs.current[index] && sign.shape && (() => {

                    const imageWidth = imageRefs.current[index].offsetWidth;
                    const imageHeight = imageRefs.current[index].offsetHeight;

                    const scaleX = imageWidth / sign.stageWidth;
                    const scaleY = imageHeight / sign.stageHeight;

                    const isCircle = sign.shape.type === "circle";

                    const shrink = 1;

                    const width = sign.shape.w * scaleX * shrink;
                    const height = sign.shape.h * scaleY * shrink;

                    // X
                    const centerX = sign.stageWidth / 2;
                    const distanceFromCenter =
                      Math.abs(sign.shape.x - centerX) / centerX;

                    const offsetX =
                      (1 * scaleX) +
                      (distanceFromCenter * 1 * scaleX);

                    // Y
                    const progress =
                      Math.max(
                        0,
                        (sign.shape.y / sign.stageHeight) - 0.5
                      ) / 0.5;

                    const offsetY =
                      (1 * scaleY) +
                      (progress * 1 * scaleY);

                    const left =
                      sign.shape.x * scaleX -
                      offsetX +
                      ((sign.shape.w * scaleX) - width) / 2;

                    const top =
                      sign.shape.y * scaleY -
                      offsetY +
                      ((sign.shape.h * scaleY) - height) / 2;

                    return (
                      <div
                        className={`shape-overlay ${isCircle ? "circle" : "square"}`}
                        style={{
                          left: `${Math.round(left)}px`,
                          top: `${Math.round(top)}px`,
                          width: `${Math.round(width)}px`,
                          height: `${Math.round(height)}px`,
                          transform: `rotate(${sign.shape.rotation}deg)`,
                          transformOrigin: "center center"
                        }}
                      />
                    );
                  })()}

                </div>
              </div>

              {/* Config */}
              <div className="sign-config">
                {/* <div className="sign-header-row">
                <h4>Sign #{index + 1}</h4> */}
                {/* <button
                  className={`ai-btn ${sign.aiMode ? "active" : ""}`}
                  onClick={() => updateSign(index, { aiMode: !sign.aiMode })}
                >
                  ✨ Create sign with AI
                </button>*/}
                {/* </div> */}

                <div className="logo-and-instructions">
                  {/* Logo */}
                  <div className={`logo-dropzone ${sign.aiMode ? "disabled" : ""} ${!sign.logo?.base64 ? "missing-logo" : ""}`}>
                    <input
                      type="file"
                      accept="image/png, image/jpeg"
                      disabled={sign.aiMode}
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;

                        if (!["image/png", "image/jpeg"].includes(file.type)) {
                          setModalMessage("Only PNG and JPG logo files are allowed.");
                          return;
                        }

                        if (file.size > 2 * 1024 * 1024) {
                          setModalMessage("Logo file is too large (maximum size is 2MB). Try uploading a screenshot instead.");
                          return;
                        }

                        const compressed = await compressImage(file);
                        const base64 = await fileToBase64(compressed);

                        updateSign(index, {
                          logo: {
                            name: file.name,
                            type: "image/jpeg",
                            base64
                          }
                        });

                      }}
                    />

                    {sign.logo?.base64 ? (
                      <img src={sign.logo.base64} alt="" />
                    ) : (
                      <div className="logo-upload-placeholder">
                        <UploadCloud size={34} />
                        <span>Drop or select logo / artwork</span>
                      </div>
                    )}
                  </div>

                  {/* Sign Instructions */}
                  <div className="sign-instructions">
                    <label>Sign Instructions (SOW)</label>

                    <button
                      type="button"
                      className={`open-instructions-btn ${openInstructionsIndex === index ? "active" : ""}`}
                      onClick={() =>
                        setOpenInstructionsIndex(openInstructionsIndex === index ? null : index)
                      }
                    >
                      {openInstructionsIndex === index ? "Hide Description" : "View / Edit Description"}
                    </button>

                    <div className={`instructions-inline-panel ${openInstructionsIndex === index ? "open" : ""}`}>
                      <textarea
                        placeholder="Instructions for this sign..."
                        value={sign.instructions || ""}
                        onChange={(e) =>
                          updateSign(index, { instructions: e.target.value })
                        }
                      />
                    </div>
                  </div>

                </div>

                <div className="sign-control-panel">

                  <div className="control-row">
                    <div className="control-label">
                      Dimensions
                      <span>Exact or approximate size</span>
                    </div>
                    <div>

                      {!sign.aiMode &&
                        <div className={`dimensions ${(!isValidDimension(sign.width) || !isValidDimension(sign.height)) ? "missing-dimensions" : ""}`} style={{ marginTop: '-10px', fontSize: '12px' }}>

                          <input
                            type="number"
                            placeholder="Width (in)"
                            value={sign.width}
                            onChange={(e) => setDimension(index, "width", e.target.value)}
                          />
                          <input
                            type="number"
                            placeholder="Height (in)"
                            value={sign.height}
                            onChange={(e) => setDimension(index, "height", e.target.value)}
                          />
                        </div>
                      }

                    </div>
                  </div>

                  <div className="control-row">
                    <div className="control-label">
                      Sign Type
                      <span>Select the main sign category</span>
                    </div>
                    <div>

                      {!sign.aiMode &&
                        SIGN_CATEGORIES.map((cat) => (
                          <div key={cat.id} className={`category ${!sign.signType ? "missing-sign-type" : ""}`}>
                            <button
                              className={`category-btn ${sign.openCategory === cat.id ? "open" : ""}`}
                              onClick={() =>
                                updateSign(index, {
                                  openCategory: sign.openCategory === cat.id ? null : cat.id
                                })
                              }
                            >
                              {cat.label}
                              <span>{sign.openCategory === cat.id ? "▲" : "▼"}</span>
                            </button>

                            <div className={`category-panel ${sign.openCategory === cat.id ? "open" : ""}`}>
                              {cat.items.map((item) => (
                                <button
                                  key={item.id}
                                  className={`type-btn ${sign.signType === item.id ? "active" : ""}`}
                                  onClick={() => {

                                    const autoNonIlluminated = ["alumCut", "acrylic3D", "pvc", "film", "banner", "printMat", "acrylicFace"];

                                    const nextIllumination =
                                      autoNonIlluminated.includes(item.id) ? false : true;

                                    const nextSign = {
                                      ...sign,
                                      signType: item.id,
                                      illuminated: nextIllumination
                                    };

                                    updateSign(index, {
                                      signType: item.id,
                                      illuminated: nextIllumination,
                                      instructions: buildSmartInstructions(nextSign)
                                    });

                                  }}

                                >
                                  {item.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}

                    </div>
                  </div>

                  <div className="control-row">
                    <div className="control-label">
                      Lighting
                      <span>Choose illumination style</span>
                    </div>
                    <div>

                      {!sign.aiMode && (
                        <div className="illumination">
                          <button
                            className={`illum-btn ${sign.illuminated === false ? "active" : ""}`}
                            onClick={() => {
                              const nextSign = {
                                ...sign,
                                illuminated: false
                              };

                              updateSign(index, {
                                illuminated: false,
                                instructions: buildSmartInstructions(nextSign)
                              });
                            }}
                          >
                            Non-illuminated
                          </button>

                          <button
                            className={`illum-btn ${sign.illuminated === true ? "active" : ""}`}
                            onClick={() => {
                              const nextSign = {
                                ...sign,
                                illuminated: true
                              };

                              updateSign(index, {
                                illuminated: true,
                                instructions: buildSmartInstructions(nextSign)
                              });
                            }}
                          >
                            Face-lit
                          </button>

                          <button
                            className={`illum-btn ${sign.illuminated === "backLit" ? "active" : ""}`}
                            onClick={() => {
                              const nextSign = {
                                ...sign,
                                illuminated: "backLit"
                              };

                              updateSign(index, {
                                illuminated: "backLit",
                                instructions: buildSmartInstructions(nextSign)
                              });
                            }}
                          >
                            Back-lit
                          </button>

                        </div>
                      )}

                    </div>
                  </div>

                  <div className="control-row">
                    <div className="control-label">
                      Additional Setup
                      <span>Backer panel, rails, color, etc.</span>
                    </div>

                    <div>

                      {!sign.aiMode && is3DSign && (
                        <div className="details-toggle">
                          <button onClick={() => updateSign(index, { openDetails: !sign.openDetails })}>
                            Dimensions & additional setup (optional)
                            <span>{sign.openDetails ? "▲" : "▼"}</span>
                          </button>

                          <div className={`details-panel ${sign.openDetails ? "open" : ""}`}>


                            <div className="additions">
                              <span>Add components (optional)</span>
                              <div className="additions-options">
                                {/* {["Backer Panel"].map((opt) => ( */}
                                {["Backer Panel", "Rails"].map((opt) => (
                                  <button
                                    key={opt}
                                    className={`addition-btn ${sign.addition === opt ? "active" : ""}`}
                                    onClick={() => {
                                      const next = sign.addition === opt ? null : opt;

                                      const nextSign = {
                                        ...sign,
                                        addition: next,
                                        additionColor: sign.additionColor || "#000000"
                                      };

                                      updateSign(index, {
                                        addition: next,
                                        additionColor: sign.additionColor || "#000000",
                                        instructions: buildSmartInstructions(nextSign)
                                      });

                                    }}
                                  >
                                    {opt}
                                  </button>
                                ))}
                              </div>
                              {sign.addition && (
                                <div className="addition-color">

                                  <label>{sign.addition} Color</label>

                                  <div className="color-presets">

                                    <button
                                      type="button"
                                      className={`color-option ${sign.additionColor === "#FFFFFF" ? "active" : ""}`}
                                      onClick={() => {
                                        const nextSign = {
                                          ...sign,
                                          additionColor: "#FFFFFF"
                                        };

                                        updateSign(index, {
                                          additionColor: "#FFFFFF",
                                          instructions: buildSmartInstructions(nextSign)
                                        });
                                      }}
                                    >
                                      <span
                                        className="color-dot"
                                        style={{ background: "#FFFFFF", border: "1px solid #ccc" }}
                                      />
                                      White
                                    </button>

                                    <button
                                      type="button"
                                      className={`color-option ${sign.additionColor === "#000000" ? "active" : ""}`}
                                      onClick={() => {
                                        const nextSign = {
                                          ...sign,
                                          additionColor: "#000000"
                                        };

                                        updateSign(index, {
                                          additionColor: "#000000",
                                          instructions: buildSmartInstructions(nextSign)
                                        });
                                      }}
                                    >
                                      <span
                                        className="color-dot"
                                        style={{ background: "#000000" }}
                                      />
                                      Black
                                    </button>

                                    <label
                                      className={`color-option custom ${!["#FFFFFF", "#000000"].includes(sign.additionColor) ? "active" : ""}`}
                                    >
                                      <div className="custom-color-header">
                                        <span
                                          className="color-custom-preview"
                                          style={{
                                            background: !["#FFFFFF", "#000000"].includes(sign.additionColor)
                                              ? sign.additionColor
                                              : "linear-gradient(135deg,#ff4d4f,#fadb14,#52c41a,#1677ff,#722ed1)"
                                          }}
                                        />

                                        <span>Custom</span>
                                      </div>

                                      <div className="custom-color-picker">
                                        <HexColorPicker
                                          color={sign.additionColor}
                                          onChange={(color) => {
                                            const nextSign = {
                                              ...sign,
                                              additionColor: color
                                            };

                                            updateSign(index, {
                                              additionColor: color,
                                              instructions: buildSmartInstructions(nextSign)
                                            });
                                          }}
                                        />
                                      </div>
                                    </label>

                                  </div>

                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                      )}
                      {!sign.aiMode && !is3DSign && (
                        <div className="details-unavailable">
                          Section available for <strong>3D Signs | Letters</strong> only.
                        </div>
                      )}
                    </div>
                  </div>
                </div>

              </div>

            </div>
          );
        })}
      </div>



      <Alert
        open={backConfirm.open}
        title="Go back?"
        message="Are you sure you want to go back? Some actions may not have been saved."
        onClose={backConfirm.cancel}
        onConfirm={backConfirm.confirm}
      />

      <Alert
        open={!!modalMessage}
        title="Missing information"
        message={modalMessage}
        onClose={() => setModalMessage("")}
        onConfirm={() => setModalMessage("")}
      />

    </div>
  );
}
